import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { PillarPage } from 'src/constants/searchPages'
import { RedirectUrls } from 'src/types/types'
import ga from 'src/utils/pixels/gtag'

interface CrossLinkLogisticsProps {
  /** Pillar of the page rendering this section. The section ONLY renders on
   *  the Warehouse and Industrial pillars — SelfStorage and Parking pillars
   *  always return null. Nationwide, always visible: unlike
   *  `CrossLinkOnDemand`, there is no partner/coverage gate here — 3PL
   *  operators are matched off-platform per lead, not tied to a fixed
   *  coverage map. */
  pillar: PillarPage
}

/** Pure gating helper — exposed for tests; the component reads its own
 *  props and forwards them here. Returns `true` only for the two B2B
 *  pillars where "bodega con servicios logísticos" intent is relevant. */
export function shouldRenderCrossLinkLogistics(pillar: PillarPage): boolean {
  return pillar === PillarPage.Warehouse || pillar === PillarPage.Industrial
}

/**
 * Editorial cross-link section on Warehouse / Industrial pillar pages that
 * points B2B renters toward Soluciones Logísticas (3PL) — bodegas and naves
 * that come bundled with logistics services (inventory control, load/unload,
 * security, fulfillment, cross-dock, etc). Structural clone of
 * `CrossLinkOnDemand.tsx`'s frame, simplified:
 *  - No coverage/partner gate — nationwide, always visible (per spec).
 *  - No price chip — 3PL services are quoted directly by the operator, never
 *    by SpotMe (four-vertex copy guardrail: no pricing claims here).
 *
 * String namespace is deliberately `common`, NOT `page_mini_bodega` (the
 * namespace `CrossLinkOnDemand` uses). `page_mini_bodega` is only SSR'd on
 * SelfStorage pillar pages — this component renders on Warehouse/Industrial
 * pillar pages, where that namespace is absent and `t()` would flash raw
 * keys. `common` is SSR'd on every hood/pillar page (hood page's
 * `getServerSideProps` namespace array, PersonalizedLanding's page wrapper),
 * so it's the only namespace safe to read from here.
 */
const CrossLinkLogistics = ({ pillar }: CrossLinkLogisticsProps) => {
  const { t } = useTranslation('common')

  if (!shouldRenderCrossLinkLogistics(pillar)) return null

  return (
    <section className="border-t border-ink-100/15 bg-cream-300 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="overflow-hidden rounded-lg border border-ink-100/15 bg-white-500">
          <div className="flex flex-col md:grid md:grid-cols-12 md:items-stretch">
            {/* Image — first on mobile, left rail on desktop */}
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-blue-500/10 md:col-span-5 md:aspect-auto md:min-h-[280px]">
              <Image
                src="/images/personalized-nave.webp"
                alt={t('LOGISTICS_CROSSLINK.EYEBROW')}
                width={760}
                height={475}
                sizes="(max-width: 900px) 100vw, 40vw"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="flex flex-col gap-4 p-6 md:col-span-7 md:justify-center md:gap-5 md:p-10">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('LOGISTICS_CROSSLINK.EYEBROW')}
              </p>
              <h2 className="font-display text-[22px] leading-[1.2] font-medium text-ink-700 md:text-[28px]">
                {t('LOGISTICS_CROSSLINK.TITLE')}
              </h2>
              <p className="max-w-[540px] text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                {t('LOGISTICS_CROSSLINK.BODY')}
              </p>
              <div className="mt-1">
                <Link
                  prefetch={false}
                  href={RedirectUrls.SolucionesLogisticas}
                  onClick={() =>
                    ga.event('pillar_crosslink_click', {
                      to: 'logistics',
                      from_pillar: pillar
                    })
                  }
                  className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-base font-semibold text-white-500 transition-all hover:bg-blue-600 active:scale-[0.98]"
                >
                  {t('LOGISTICS_CROSSLINK.CTA')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(CrossLinkLogistics)
